import { ref, computed, onMounted, onUnmounted } from 'vue';

type ThemeMode = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'theme-preference';

// Shared state across components
const themePreference = ref<ThemeMode>('system');

function getSystemPrefersDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function applyTheme() {
  const isDarkMode =
    themePreference.value === 'dark' ||
    (themePreference.value === 'system' && getSystemPrefersDark());

  if (isDarkMode) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}

export function useTheme() {
  const isDark = computed(() => {
    return (
      themePreference.value === 'dark' ||
      (themePreference.value === 'system' && getSystemPrefersDark())
    );
  });

  function setTheme(mode: ThemeMode) {
    themePreference.value = mode;
    localStorage.setItem(STORAGE_KEY, mode);
    applyTheme();
  }

  function cycleTheme() {
    const modes: ThemeMode[] = ['system', 'light', 'dark'];
    const currentIndex = modes.indexOf(themePreference.value);
    const nextIndex = (currentIndex + 1) % modes.length;
    setTheme(modes[nextIndex]);
  }

  // Listen for system preference changes
  let mediaQuery: MediaQueryList | null = null;
  const handleSystemChange = () => {
    if (themePreference.value === 'system') {
      applyTheme();
    }
  };

  onMounted(() => {
    // Load saved preference
    const saved = localStorage.getItem(STORAGE_KEY) as ThemeMode | null;
    if (saved && ['system', 'light', 'dark'].includes(saved)) {
      themePreference.value = saved;
    }
    applyTheme();

    // Watch for system theme changes
    mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', handleSystemChange);
  });

  onUnmounted(() => {
    if (mediaQuery) {
      mediaQuery.removeEventListener('change', handleSystemChange);
    }
  });

  return {
    themePreference,
    isDark,
    setTheme,
    cycleTheme,
  };
}
